/**
 * Epic 12 Phase A: Global banner for degraded/unknown operational state.
 * Shows "What's wrong?" + next actions (Go to Diagnostics, Copy context).
 */

import React from 'react';
import { Alert, Box, Button, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
  type OpsState,
  opsStateLabel,
  opsStateColor,
} from '../services/opsState';

export interface OpsStateReason {
  title: string;
  detail?: string;
  severity?: 'info' | 'warning' | 'error';
}

export interface OpsStateBannerProps {
  state: OpsState;
  reasons: OpsStateReason[];
  /** Replaces the default "{label}. {reasons}" headline when set. */
  headline?: string;
  /** Primary action: navigate (to) or custom (onClick). */
  primaryCta?: { label: string; to?: string; onClick?: () => void };
  secondaryCta?: { label: string; onClick: () => void };
}

/**
 * Renders only when state !== 'healthy'.
 * "Unknown" copy is non-alarming ("signals missing", not "broken").
 */
export const OpsStateBanner: React.FC<OpsStateBannerProps> = ({
  state,
  reasons,
  headline,
  primaryCta,
  secondaryCta,
}) => {
  const navigate = useNavigate();

  if (state === 'healthy') return null;

  const severity = opsStateColor(state) === 'error' ? 'error' : opsStateColor(state) === 'warning' ? 'warning' : 'info';
  const label = opsStateLabel(state);

  return (
    <Alert
      severity={severity}
      sx={{ mb: 2 }}
      action={
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {primaryCta && (
            <Button
              color="inherit"
              size="small"
              onClick={
                primaryCta.onClick
                  ? primaryCta.onClick
                  : () => primaryCta!.to && navigate(primaryCta!.to)
              }
            >
              {primaryCta.label}
            </Button>
          )}
          {secondaryCta && (
            <Button
              color="inherit"
              size="small"
              variant="outlined"
              onClick={secondaryCta.onClick}
            >
              {secondaryCta.label}
            </Button>
          )}
        </Box>
      }
    >
      <Typography component="span" variant="body2">
        {headline ? (
          headline
        ) : (
          <>
            <strong>{label}.</strong>{' '}
            {state === 'unknown'
              ? 'No recent API responses yet. This is normal on fresh load. Check connectivity or refresh.'
              : reasons.length > 0
                ? reasons.map((r) => r.title).join('; ')
                : 'System may be experiencing issues.'}
          </>
        )}
      </Typography>
    </Alert>
  );
};
